/**
 * ToolTrace — the collapsible tool trace of one Ion Next turn (admin sees
 * tool I/O unredacted, as on every admin surface).
 *
 *   - `code_execution` cards (native/files.execution_record): the code that
 *     ran, the trimmed stdout / stderr, the execution id and the file ids.
 *   - web cards (`server_web_search` / `server_web_fetch`, native/stream_map.web_record):
 *     the query or URL and the result titles as links.
 *   - every other call: the existing ToolCallCard, with the provenance label
 *     in its head when its artifact is a derived / estimate / recommendation chip.
 *     A clarifying question (`ask_user` artifact, Ion Next's `ask_clarification`)
 *     is shown as plain JSON here: its dialog renders once, under the answer
 *     (IonNextAssistantBlock), and ToolCallCard's own dialog is wired to the
 *     customer chat.
 */
import { useState } from 'preact/hooks';
import { ToolCallCard } from '../../chat/ToolCallCard.jsx';
import { chipProvenanceInfo } from '../../chat/chipProvenance.js';
import { unwrapArtifact } from './provenance.js';

const CODE_EXECUTION = 'code_execution';
const WEB_SEARCH = 'server_web_search';
const WEB_FETCH = 'server_web_fetch';

function duration(call) {
  if (!call.startedAt || !call.endedAt) return null;
  const ms = call.endedAt - call.startedAt;
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(2)} s`;
}

function Block({ title, text, tone }) {
  if (text == null || text === '') return null;
  return (
    <section className="ion-tool-card__section">
      <h4 className="ion-tool-card__section-title">{title}</h4>
      <pre className={`ion-tool-card__code${tone ? ` ion-next-trace__${tone}` : ''}`}>{String(text)}</pre>
    </section>
  );
}

function CodeExecutionEntry({ call }) {
  const [open, setOpen] = useState(false);
  const out = (call.output && (call.output.result || call.output)) || {};
  const fileIds = Array.isArray(out.file_ids) ? out.file_ids : [];
  const returnCode = out.return_code ?? out.returncode ?? null;
  const d = duration(call);
  return (
    <div className={`ion-tool-card ${call.isError ? 'ion-tool-card--error' : call.status === 'running' ? 'ion-tool-card--running' : 'ion-tool-card--done'}`}>
      <button type="button" className="ion-tool-card__head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="ion-tool-card__chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="ion-tool-card__name">code_execution</span>
        {out.execution_id && <span className="ion-next-trace__id">{out.execution_id}</span>}
        {returnCode != null && returnCode !== 0 && (
          <span className="ion-tool-card__status ion-tool-card__status--error">exit {returnCode}</span>
        )}
        {fileIds.length > 0 && <span className="ion-next-trace__id">{fileIds.length} file(s)</span>}
        {d && <span className="ion-tool-card__duration">{d}</span>}
      </button>
      {open && (
        <div className="ion-tool-card__body">
          <Block title="Code" text={out.code} />
          <Block title="stdout" text={out.stdout} />
          <Block title="stderr" text={out.stderr} tone="stderr" />
          {fileIds.length > 0 && <Block title="Files" text={fileIds.join('\n')} />}
        </div>
      )}
    </div>
  );
}

function safeHref(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
}

function WebLink({ url, title }) {
  const href = safeHref(url);
  const label = title || url || '';
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>
  ) : (
    <span>{label}</span>
  );
}

function WebEntry({ call }) {
  const [open, setOpen] = useState(false);
  const out = (call.output && (call.output.result || call.output)) || {};
  const inp = call.input || {};
  const search = call.name === WEB_SEARCH;
  const results = Array.isArray(out.results) ? out.results : [];
  // An error record keeps only its code: the query / URL come from the input.
  const target = search ? out.query || inp.query : out.url || inp.url;
  const errorText = out.error_code || (call.isError ? out.error || 'error' : null);
  const d = duration(call);
  return (
    <div className={`ion-tool-card ${call.isError ? 'ion-tool-card--error' : 'ion-tool-card--done'}`}>
      <button type="button" className="ion-tool-card__head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="ion-tool-card__chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span className="ion-tool-card__name">{search ? 'web_search' : 'web_fetch'}</span>
        <span className="ion-next-trace__id">{target}</span>
        {errorText && (
          <span className="ion-tool-card__status ion-tool-card__status--error">{errorText}</span>
        )}
        {search && !errorText && <span className="ion-next-trace__id">{results.length} result(s)</span>}
        {d && <span className="ion-tool-card__duration">{d}</span>}
      </button>
      {open && (
        <div className="ion-tool-card__body">
          {search ? (
            <ul className="ion-next-trace__links">
              {results.map((r, i) => (
                <li key={i}><WebLink url={r.url} title={r.title} /></li>
              ))}
            </ul>
          ) : (
            <p className="ion-next-trace__links"><WebLink url={out.url || inp.url} title={out.title} /></p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The call as the trace shows it: an `ask_user` artifact loses its
 * `artifact_type`, so ToolCallCard prints it as JSON instead of rendering a
 * second, customer-wired dialog. Every other call is returned unchanged.
 */
export function withoutAskDialog(call) {
  const artifact = unwrapArtifact(call);
  if (!artifact || artifact.artifact_type !== 'ask_user') return call;
  const rest = { ...artifact };
  delete rest.artifact_type;
  const output = call.output.artifact_type ? rest : { ...call.output, result: rest };
  return { ...call, output };
}

export function ProvenanceBadge({ artifact }) {
  const info = chipProvenanceInfo(artifact && artifact.provenance);
  if (!info) return null;
  return (
    <span
      className={`ion-next-prov-badge ion-next-prov-badge--${info.cssKind}`}
      title={[info.title, ...info.lines].join('\n')}
    >
      {info.chipLabel}
    </span>
  );
}

/**
 * @param {object} props
 * @param {Array<object>} props.toolCalls — the turn's tool calls (useChatStream shape)
 */
export function ToolTrace({ toolCalls }) {
  const [open, setOpen] = useState(false);
  const calls = toolCalls || [];
  if (calls.length === 0) return null;
  const executions = calls.filter((c) => c.name === CODE_EXECUTION).length;
  const searches = calls.filter((c) => c.name === WEB_SEARCH || c.name === WEB_FETCH).length;
  const errors = calls.filter((c) => c.isError).length;
  return (
    <div className="ion-next-trace">
      <button
        type="button"
        className="ion-next-trace__toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span> Tool trace · {calls.length} call
        {calls.length === 1 ? '' : 's'}
        {executions > 0 && ` · ${executions} code execution${executions === 1 ? '' : 's'}`}
        {searches > 0 && ` · ${searches} web call${searches === 1 ? '' : 's'}`}
        {errors > 0 && ` · ${errors} error${errors === 1 ? '' : 's'}`}
      </button>
      {open && (
        <div className="ion-next-trace__list">
          {calls.map((call) =>
            call.name === CODE_EXECUTION ? (
              <CodeExecutionEntry key={call.id} call={call} />
            ) : call.name === WEB_SEARCH || call.name === WEB_FETCH ? (
              <WebEntry key={call.id} call={call} />
            ) : (
              <ToolCallCard
                key={call.id}
                toolCall={withoutAskDialog(call)}
                badge={call.isError ? null : <ProvenanceBadge artifact={unwrapArtifact(call)} />}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}
